import { useMemo, useRef, useState } from 'react';
import { c, card, chart, z } from '../../theme';
import { COAST, ISLANDS } from './cityMapCoast';
import { fmtCount } from './format';

// Where the window's calls came from, as a circle per place over the coastline.
//
// It sits under the Board's top-ten table rather than replacing it: the table
// carries exact figures and the split-yard caveats, and this carries the tail
// the table cuts off and the geography nobody can read out of a list.
//
// Circle AREA is proportional to calls — radius goes with the square root —
// because a circle whose radius doubled would read as four times the calls.
//
// Hand-placed points (a City of LA franchise zone, a neighbourhood, a campus:
// anything that is not a Census place) are drawn hollow and dashed. A point
// that was placed by judgment should not look like one that was looked up.
// And what cannot be placed at all — a whole county, a contract type — is
// listed under the map with its calls, because a map that quietly drops what it
// cannot draw looks more complete than it is. Where every point came from is in
// backend/src/services/ccCityPoints.js.

const W = 900;
const H = 390;
const KX = Math.cos((34 * Math.PI) / 180);   // longitude shrinks with latitude
const MAX_R = 22;
const MIN_R = 3.5;
const LABELS = 8;
const LABEL_H = 13;
const CHAR_W = 6.6;                          // at the labels' 12px, near enough to place by

/** Bounds that hold every point with a margin, stretched to the drawing's shape. */
export function fitBounds(points) {
  if (!points.length) return { w: -118.9, e: -117.4, s: 33.7, n: 34.3 };
  const lats = points.map((p) => p.lat);
  const lons = points.map((p) => p.lon);
  const pad = 0.05;
  let s = Math.min(...lats) - pad; let n = Math.max(...lats) + pad;
  let w = Math.min(...lons) - pad; let e = Math.max(...lons) + pad;
  // Grow whichever span is short so the map keeps W:H without distorting.
  const needLon = ((n - s) * (W / H)) / KX;
  const needLat = ((e - w) * KX * H) / W;
  if (needLon > e - w) { const d = (needLon - (e - w)) / 2; w -= d; e += d; }
  if (needLat > n - s) { const d = (needLat - (n - s)) / 2; s -= d; n += d; }
  return { w, e, s, n };
}

/**
 * Labels for the biggest circles, each tried right, left, above and below, and
 * dropped rather than drawn over another label or another circle. Placement is
 * estimated from character count, not measured, so it is the same on every
 * machine and can be tested without a browser.
 */
export function placeLabels(circles, { max = LABELS, width = W, height = H, charW = CHAR_W } = {}) {
  const boxes = [];
  const out = [];
  const hitsCircle = (b, self) => circles.some((o) => {
    if (o === self) return false;
    const cx = Math.max(b.x0, Math.min(o.x, b.x1));
    const cy = Math.max(b.y0, Math.min(o.y, b.y1));
    return (o.x - cx) ** 2 + (o.y - cy) ** 2 < (o.r + 1) ** 2;
  });
  for (const ci of circles.slice(0, max)) {
    const w = ci.text.length * charW;
    const tries = [
      { x: ci.x + ci.r + 4, y: ci.y + 4, anchor: 'start', b: { x0: ci.x + ci.r + 4, x1: ci.x + ci.r + 4 + w, y0: ci.y - 9, y1: ci.y + 4 } },
      { x: ci.x - ci.r - 4, y: ci.y + 4, anchor: 'end', b: { x0: ci.x - ci.r - 4 - w, x1: ci.x - ci.r - 4, y0: ci.y - 9, y1: ci.y + 4 } },
      { x: ci.x, y: ci.y - ci.r - 5, anchor: 'middle', b: { x0: ci.x - w / 2, x1: ci.x + w / 2, y0: ci.y - ci.r - 5 - LABEL_H, y1: ci.y - ci.r - 5 } },
      { x: ci.x, y: ci.y + ci.r + 13, anchor: 'middle', b: { x0: ci.x - w / 2, x1: ci.x + w / 2, y0: ci.y + ci.r + 1, y1: ci.y + ci.r + 14 } },
    ];
    const fit = tries.find(({ b }) => b.x0 >= 2 && b.x1 <= width - 2 && b.y0 >= 2 && b.y1 <= height - 2
      && !boxes.some((o) => b.x0 < o.x1 && b.x1 > o.x0 && b.y0 < o.y1 && b.y1 > o.y0)
      && !hitsCircle(b, ci));
    if (!fit) continue;
    boxes.push(fit.b);
    out.push({ id: ci.id, text: ci.text, x: fit.x, y: fit.y, anchor: fit.anchor });
  }
  return out;
}

/**
 * Three round figures for the size key, spanning what the map actually draws.
 * Rounded DOWN: a key circle for 4,000 on a map whose biggest place took 3,600
 * would label a size nothing on the map reaches.
 */
export function sizeKey(vmax) {
  const down = (v) => {
    if (v < 1) return 1;
    const mag = 10 ** Math.floor(Math.log10(v));
    return Math.floor(v / mag) * mag;
  };
  return [...new Set([down(vmax / 10), down(vmax / 3), down(vmax)])];
}

export default function CityMap({ cityMap, rangeLabel }) {
  const [hover, setHover] = useState(null);
  const [tipAt, setTipAt] = useState(null);
  const fig = useRef(null);
  const points = useMemo(() => cityMap?.points || [], [cityMap]);
  const unplaced = cityMap?.unplaced || [];

  const geo = useMemo(() => {
    const b = fitBounds(points);
    const px = (lon) => ((lon - b.w) / (b.e - b.w)) * W;
    const py = (lat) => ((b.n - lat) / (b.n - b.s)) * H;
    const path = (pts) => pts.map(([la, lo], i) => `${i ? 'L' : 'M'}${px(lo).toFixed(1)},${py(la).toFixed(1)}`).join('');
    // The coast runs NW -> SE with land on its left; close it round the far
    // side, well outside the drawing, and it becomes the land.
    const far = [[b.s - 1, b.e + 1], [b.n + 1, b.e + 1], [b.n + 1, b.w - 1]];
    const vmax = Math.max(1, ...points.map((p) => p.volume));
    const radius = (v) => Math.max(MIN_R, MAX_R * Math.sqrt(v / vmax));
    const circles = points.map((p, i) => ({ id: i, x: px(p.lon), y: py(p.lat), r: radius(p.volume), text: p.place, p }));
    return {
      land: path([...COAST, ...far]) + 'Z',
      coast: path(COAST),
      islands: ISLANDS.map((r) => path(r) + 'Z'),
      circles,
      labels: placeLabels(circles),
      vmax,
      radius,
    };
  }, [points]);

  if (!points.length && !unplaced.length) return null;

  const total = (cityMap?.placedVolume || 0) + (cityMap?.unplacedVolume || 0);
  const pctOf = (n) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '—');
  const hovered = hover == null ? null : geo.circles[hover];

  const show = (i, e) => {
    const r = fig.current?.getBoundingClientRect();
    setHover(i);
    if (r) setTipAt({ x: e.clientX - r.left, y: e.clientY - r.top, w: r.width });
  };

  return (
    <section style={s.card} aria-label="City map">
      <style>{css}</style>
      <h2 style={s.h2}>Where calls came from <span style={s.sub}>({rangeLabel})</span></h2>

      <figure ref={fig} style={s.fig} onPointerLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} style={s.svg} role="img"
          aria-label={`Map of ${points.length} places that took calls. `
            + points.slice(0, 10).map((p) => `${p.place} ${fmtCount(p.volume)}`).join(', ')}>
          <rect x="0" y="0" width={W} height={H} fill={c.infoTint} />
          <path d={geo.land} fill={c.surface} />
          {geo.islands.map((d, i) => <path key={i} d={d} fill={c.surface} stroke={c.borderStrong} strokeWidth="1" />)}
          <path d={geo.coast} fill="none" stroke={c.borderStrong} strokeWidth="1.2" />

          {/* Biggest first, so a small place drawn later is never buried under a big one. */}
          {geo.circles.map((ci) => (
            <circle key={ci.id} cx={ci.x} cy={ci.y} r={ci.r} vectorEffect="non-scaling-stroke"
              fill={ci.p.handPlaced ? c.surface : BLUE} fillOpacity={ci.p.handPlaced ? 1 : 0.8}
              stroke={hover === ci.id ? c.text : (ci.p.handPlaced ? BLUE : c.surface)}
              strokeWidth={hover === ci.id ? 2.2 : 1.5}
              strokeDasharray={ci.p.handPlaced && hover !== ci.id ? '3 2.5' : undefined} />
          ))}

          <g className="cc-citymap-labels">
            {geo.labels.map((l) => (
              <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} style={s.label}>{l.text}</text>
            ))}
          </g>

          {/* Hit targets at least 9 units across, drawn in the same order so the
              smallest stays reachable where circles overlap. */}
          {geo.circles.map((ci) => (
            <circle key={`h${ci.id}`} data-city-point={ci.p.place} cx={ci.x} cy={ci.y} r={Math.max(ci.r, 9)}
              fill="transparent" style={{ cursor: 'pointer' }}
              onPointerEnter={(e) => show(ci.id, e)} onPointerMove={(e) => show(ci.id, e)}
              onMouseOver={(e) => show(ci.id, e)} onMouseOut={() => setHover(null)} />
          ))}
        </svg>

        {hovered && (
          <div role="status" style={{ ...s.tip, ...tipPosition(tipAt) }}>
            <strong style={s.tipTitle}>{hovered.p.place}</strong>
            <span style={s.tipRow}>{fmtCount(hovered.p.volume)} calls{hovered.p.sharePct != null ? ` · ${hovered.p.sharePct}% of Handled` : ''}</span>
            {hovered.p.yard && (
              <span style={s.tipMuted}>Yard {hovered.p.yard}{hovered.p.yardSharePct != null && hovered.p.yardSharePct < 100 ? ` (~${Math.round(hovered.p.yardSharePct)}% of it)` : ''}</span>
            )}
            {hovered.p.keys.length > 1 && hovered.p.keys.map((k) => (
              <span key={k.city} style={s.tipMuted}>{k.city} · {fmtCount(k.volume)}</span>
            ))}
            {hovered.p.handPlaced && <span style={s.tipMuted}>Placed by hand: not a Census place</span>}
          </div>
        )}
      </figure>

      <div style={s.legend}>
        <span style={s.legendItem}>
          <svg width={legendWidth(geo)} height={Math.ceil(geo.radius(geo.vmax) * 2) + 2} aria-hidden="true">
            {legendCircles(geo)}
          </svg>
          calls
        </span>
        <span style={s.legendItem}>
          <svg width="16" height="16" aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" fill={c.surface} stroke={BLUE} strokeWidth="1.5" strokeDasharray="3 2.5" />
          </svg>
          Placed by hand
        </span>
      </div>

      {unplaced.length > 0 && (
        <details style={s.details}>
          <summary style={s.summary}>
            Not on the map: {fmtCount(cityMap.unplacedVolume)} calls ({pctOf(cityMap.unplacedVolume)}) from {unplaced.length} {unplaced.length === 1 ? 'key' : 'keys'} with no single location
          </summary>
          <ul style={s.list}>
            {unplaced.map((u) => (
              <li key={u.city} style={s.listRow}><span>{u.city}</span><span style={s.muted}>{u.reason}</span><span style={s.num}>{fmtCount(u.volume)}</span></li>
            ))}
          </ul>
        </details>
      )}

      <details style={s.details}>
        <summary style={s.summary}>Every place on the map ({points.length})</summary>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Place</th><th style={s.thNum}>Calls</th><th style={s.thNum}>% of Handled</th><th style={s.th}>Yard</th></tr></thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.place}>
                  <td style={s.td}>{p.place}{p.handPlaced ? <span style={s.muted}> · placed by hand</span> : ''}</td>
                  <td style={s.tdNum}>{fmtCount(p.volume)}</td>
                  <td style={s.tdNum}>{p.sharePct != null ? `${p.sharePct}%` : '—'}</td>
                  <td style={s.td}>{p.yard || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <p style={s.foot}>
        Circle area is proportional to calls handled, from the same Voice, VIP and Callback calls as the table above.
        Places are Census Bureau points; hollow circles were placed by hand for areas that are not Census places,
        such as the City of LA&rsquo;s franchise zones. A city&rsquo;s contract lines (residential, commercial, open
        market) are drawn as one circle.
      </p>
    </section>
  );
}

const BLUE = chart.categorical[0];

function tipPosition(at) {
  if (!at) return { left: 8, top: 8 };
  // Right of the pointer, flipped left near the right edge; above it where
  // there is room. Estimated rather than measured: the chip has no size until
  // it is laid out.
  const left = at.x > at.w - 230 ? Math.max(4, at.x - 230) : at.x + 14;
  const top = at.y > 90 ? at.y - 80 : at.y + 16;
  return { left, top };
}

function legendCircles(geo) {
  let x = 0;
  const hMax = geo.radius(geo.vmax) * 2 + 1;
  return sizeKey(geo.vmax).map((v) => {
    const r = geo.radius(v);
    const cx = x + r;
    x += r * 2 + 44;
    return (
      <g key={v}>
        <circle cx={cx} cy={hMax - r} r={r} fill={BLUE} fillOpacity="0.8" stroke={c.surface} strokeWidth="1.5" />
        <text x={cx + r + 4} y={hMax - r + 4} style={{ fontSize: '11px', fill: c.textMuted }}>{fmtCount(v)}</text>
      </g>
    );
  });
}

function legendWidth(geo) {
  return sizeKey(geo.vmax).reduce((w, v) => w + geo.radius(v) * 2 + 44, 0);
}

// Labels shrink with the drawing; below a phone's width they are too small to
// read, and the tooltip and the table carry the names instead.
const css = `
@media (max-width: 560px) { .cc-citymap-labels { display: none; } }
`;

const s = {
  card:    { ...card.base, padding: '14px 18px 12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  h2:      { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  sub:     { fontWeight: 400, color: c.textMuted },
  fig:     { position: 'relative', margin: 0, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${c.border}` },
  svg:     { display: 'block', width: '100%', height: 'auto' },
  label:   { fontSize: '12px', fontWeight: 600, fill: c.textStrong, paintOrder: 'stroke', stroke: c.surface,
    strokeWidth: '3.5px', strokeLinejoin: 'round', pointerEvents: 'none' },
  tip:     { position: 'absolute', zIndex: z.menu, pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: '1px',
    background: c.navy, color: c.onNavy, borderRadius: '7px', padding: '7px 10px', fontSize: '12px', lineHeight: 1.4,
    maxWidth: '220px', boxShadow: '0 8px 22px rgba(15,23,42,0.28)' },
  tipTitle: { fontWeight: 700, fontSize: '12.5px' },
  tipRow:  { fontVariantNumeric: 'tabular-nums' },
  tipMuted: { color: c.onNavyMuted, fontVariantNumeric: 'tabular-nums' },
  legend:  { display: 'flex', flexWrap: 'wrap', gap: '8px 22px', alignItems: 'center', fontSize: '11.5px', color: c.textMuted },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '7px' },
  details: { fontSize: '12px', color: c.textStrong },
  summary: { cursor: 'pointer', color: c.textStrong, fontWeight: 500 },
  list:    { listStyle: 'none', margin: '6px 0 0', padding: 0 },
  listRow: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 2fr) auto', gap: '12px', padding: '4px 0',
    borderBottom: `1px dashed ${c.border}` },
  muted:   { color: c.textMuted },
  num:     { fontVariantNumeric: 'tabular-nums', textAlign: 'right' },
  tableWrap: { overflowX: 'auto', marginTop: '6px' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: c.textMuted, padding: '5px 6px', borderBottom: `1px solid ${c.border}` },
  thNum:   { textAlign: 'right', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: c.textMuted, padding: '5px 6px', borderBottom: `1px solid ${c.border}` },
  td:      { padding: '5px 6px', borderBottom: `1px solid ${c.border}`, color: c.textStrong },
  tdNum:   { padding: '5px 6px', borderBottom: `1px solid ${c.border}`, color: c.textStrong, textAlign: 'right',
    fontVariantNumeric: 'tabular-nums' },
  foot:    { fontSize: '11px', color: c.textMuted, margin: 0, lineHeight: 1.45 },
};
